import React, { useState, useMemo } from 'react';
import { Billboard, Text, Html } from '@react-three/drei';
import { SlotInfo } from './tipos';
import { Pallet3D } from './Pallet3D';

interface SlotRack3DProps {
  slot: SlotInfo;
  position: [number, number, number];
  anchoSlot?: number;
  altoSlot?: number;
  profundidadSlot?: number;
  isModoReubicacion?: boolean;
  isSlotOrigen?: boolean;
  onSelectSlot: (slot: SlotInfo) => void;
}

export const SlotRack3D: React.FC<SlotRack3DProps> = ({
  slot,
  position,
  anchoSlot = 1.25,
  altoSlot = 1.2,
  profundidadSlot = 1.05,
  isModoReubicacion = false,
  isSlotOrigen = false,
  onSelectSlot,
}) => {
  const [hovered, setHovered] = useState(false);

  // Paleta de color del pallet según estado logístico
  const palletColor = useMemo(() => {
    switch (slot.estadoPrincipal) {
      case 'Dañado':
      case 'Cuarentena':
        return '#dc2626'; // Rojo
      case 'Reservado':
        return '#3b82f6'; // Azul eléctrico
      default:
        return '#2563eb'; // Azul estándar
    }
  }, [slot.estadoPrincipal]);

  const showHologram = !slot.isOccupied && (hovered || isModoReubicacion);

  return (
    <group
      position={position}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
        document.body.style.cursor = 'auto';
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectSlot(slot);
      }}
    >
      {/* 1. HITBOX TRANSPARENTE DEL SLOT (Para captura milimétrica del Raycast) */}
      <mesh position={[0, altoSlot / 2, 0]} visible={false}>
        <boxGeometry args={[anchoSlot, altoSlot, profundidadSlot]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* 2. SI ESTÁ OCUPADO: RENDERIZA EL PALLET CON SU CARGA */}
      {slot.isOccupied ? (
        <group>
          <Pallet3D
            position={[0, 0, 0]}
            color={palletColor}
            items={slot.items}
            cantidadTotal={slot.cantidadTotal}
            isDamaged={slot.estadoPrincipal === 'Dañado' || slot.estadoPrincipal === 'Cuarentena'}
            isReserved={slot.estadoPrincipal === 'Reservado'}
            anchoSlot={anchoSlot}
          />

          {/* Marcador de origen si se está reubicando */}
          {isSlotOrigen && (
            <mesh position={[0, altoSlot / 2, 0]}>
              <boxGeometry args={[anchoSlot * 0.95, altoSlot * 0.9, profundidadSlot * 0.95]} />
              <meshBasicMaterial color="#f59e0b" wireframe />
            </mesh>
          )}

          {/* Plaqueta LPN en el frontal del larguero */}
          <Billboard position={[0, -0.06, profundidadSlot / 2 + 0.04]}>
            <mesh position={[0, 0, -0.005]}>
              <planeGeometry args={[Math.min(0.58, anchoSlot * 0.88), 0.15]} />
              <meshBasicMaterial color="#09090b" opacity={0.92} transparent />
            </mesh>
            <Text
              fontSize={Math.min(0.075, anchoSlot * 0.14)}
              color="#38bdf8"
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
            >
              {`S${slot.posicion} · ${slot.lpn.slice(-8)}`}
            </Text>
          </Billboard>
        </group>
      ) : (
        /* 3. SI ESTÁ VACÍO: HUECO DISPONIBLE / HOLOGRAMA */
        <group>
          {showHologram && (
            <group position={[0, altoSlot / 2, 0]}>
              {/* Caja holográfica punteada */}
              <mesh>
                <boxGeometry args={[anchoSlot * 0.92, altoSlot * 0.85, profundidadSlot * 0.92]} />
                <meshBasicMaterial
                  color={isModoReubicacion ? '#10b981' : '#38bdf8'}
                  wireframe
                  transparent
                  opacity={isModoReubicacion ? 0.7 : 0.35}
                />
              </mesh>
              <mesh>
                <boxGeometry args={[anchoSlot * 0.9, altoSlot * 0.83, profundidadSlot * 0.9]} />
                <meshBasicMaterial
                  color={isModoReubicacion ? '#10b981' : '#38bdf8'}
                  transparent
                  opacity={isModoReubicacion ? 0.2 : 0.08}
                />
              </mesh>
            </group>
          )}

          {/* Marcador de Celda Vacía en el Larguero */}
          <Billboard position={[0, -0.06, profundidadSlot / 2 + 0.04]}>
            <mesh position={[0, 0, -0.005]}>
              <planeGeometry args={[0.38, 0.14]} />
              <meshBasicMaterial
                color={isModoReubicacion ? '#065f46' : '#1e293b'}
                opacity={0.85}
                transparent
              />
            </mesh>
            <Text
              fontSize={0.07}
              color={isModoReubicacion ? '#34d399' : '#94a3b8'}
              anchorX="center"
              anchorY="middle"
              fontWeight="bold"
            >
              {isModoReubicacion ? `📥 S${slot.posicion}` : `S${slot.posicion} [Libre]`}
            </Text>
          </Billboard>
        </group>
      )}

      {/* 4. TOOLTIP FLOTANTE AL HACER HOVER EN EL SLOT */}
      {hovered && (
        <Html
          position={[0, altoSlot + 0.3, 0]}
          center
          distanceFactor={16}
          zIndexRange={[100, 0]}
        >
          <div className="bg-slate-900/95 text-white p-2.5 rounded-xl shadow-2xl border border-cyan-500/60 backdrop-blur-md min-w-[200px] pointer-events-none transform -translate-y-2 transition-all">
            <div className="flex items-center justify-between border-b border-slate-700/80 pb-1 mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400">
                Nivel {slot.nivel} · Posición {slot.posicion}
              </span>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                  slot.isOccupied
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                    : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                }`}
              >
                {slot.isOccupied ? 'Ocupado' : 'Disponible'}
              </span>
            </div>

            {slot.isOccupied ? (
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between text-slate-300">
                  <span>LPN:</span>
                  <span className="font-mono font-bold text-white">{slot.lpn}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Stock:</span>
                  <span className="font-bold text-white">{slot.cantidadTotal} u</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Peso:</span>
                  <span className="font-bold text-emerald-400">{slot.pesoTotalKg.toFixed(1)} kg</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>SKU:</span>
                  <span className="font-bold text-blue-300 truncate max-w-[110px]">
                    {slot.items[0]?.sku || 'Varios'}
                  </span>
                </div>
                <div className="pt-1 border-t border-slate-700/60 text-[9px] text-center text-cyan-400 font-bold">
                  🖱️ Clic para gestionar pallet
                </div>
              </div>
            ) : (
              <div className="text-[10px] text-center text-slate-300 py-0.5">
                {isModoReubicacion ? (
                  <span className="text-emerald-400 font-bold animate-pulse">
                    📥 Clic para colocar el Pallet aquí
                  </span>
                ) : (
                  <span>Hueco disponible para Putaway</span>
                )}
              </div>
            )}
          </div>
        </Html>
      )}
    </group>
  );
};
